import React from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { Gender, RiderStatus } from "../../../constants/constants";
import getCountryISO2 from "country-iso-3-to-2";
import { formatCurrency, calculateRiderValue } from "../../../utils/utils";
import { Rating, Rider } from "../../../types/model";
import { FaUserInjured } from "react-icons/fa";
import RiderHistoryButton from "./RiderHistoryButton";

interface BudgetModeEligibleRiderCardProps {
	rider: Rider;
	sport: string;
	isAddDisabled: (gender: Gender, rider: Rider) => boolean;
	addRiderToTeam: (rider: Rider) => void;
}

const BudgetModeEligibleRiderCard: React.FC<
	BudgetModeEligibleRiderCardProps
> = ({ rider, sport, isAddDisabled, addRiderToTeam }) => {
	return (
		<Card
			key={rider._id}
			className="mb-1 py-0 shadow bg-dark text-white border-0"
		>
			<Card.Body className="py-1 my-0">
				{/* Desktop layout (md and above) */}
				<div className="d-none d-md-block">
					<Row className="align-items-center flex-nowrap py-0 my-0 text-sm-start text-center">
						<Col md={5} className="text-start my-2 my-sm-0 overflow-hidden">
							<div className="d-flex align-items-center gap-2 overflow-hidden">
								{getCountryISO2((rider.country ?? "").toUpperCase().trim()) ? (
									<ReactCountryFlag
										countryCode={getCountryISO2(
											(rider.country ?? "").toUpperCase().trim(),
										)}
										svg
										style={{ width: "2rem", minWidth: "2rem" }}
									/>
								) : (
									<span className="ms-2 text-nowrap">{rider.country}</span>
								)}
								<div className="text-truncate text-nowrap">
									<b>{rider.last_name}</b> {rider.first_name}
									<span style={{ fontSize: "0.6rem" }} className="ms-1">
										({rider.gender})
									</span>
									{rider.status === RiderStatus.injured && (
										<span className="ms-2" style={{ fontSize: "0.8rem" }}>
											<FaUserInjured className="mb-1" /> Injured
										</span>
									)}
								</div>
							</div>
						</Col>
						<Col md={3} className="text-center text-nowrap">
							{(rider.ratings ?? []).map((rating: Rating) => {
								if (rating.sport.toString() === sport) {
									return (
										<span key={rating._id}>
											{formatCurrency(calculateRiderValue(rating.rating))}
										</span>
									);
								}
								return null;
							})}
						</Col>
						<Col md={4} className="d-flex justify-content-end">
							<div className="d-flex gap-1 justify-content-end w-100">
								<RiderHistoryButton
									riderId={rider._id}
									riderName={`${rider.first_name} ${rider.last_name}`}
									initialRider={rider}
									size="sm"
									className="px-2 flex-shrink-0"
									hideIcon
								/>
								<Button
									variant={
										isAddDisabled(rider.gender as Gender, rider)
											? "secondary"
											: "success"
									}
									size="sm"
									className="text-light px-2 flex-shrink-0"
									onClick={() => addRiderToTeam(rider)}
									disabled={isAddDisabled(rider.gender as Gender, rider)}
								>
									Add to Team
								</Button>
							</div>
						</Col>
					</Row>
				</div>

				{/* Mobile layout (smaller than md) */}
				<div className="d-md-none">
					<Row className="align-items-center py-0 my-0">
						<Col
							xs={7}
							className="text-start p-2"
							style={{ fontSize: "0.8rem" }}
						>
							<div className="d-flex flex-row">
								<b className="me-1">{rider.last_name}</b> {rider.first_name}{" "}
								<span style={{ fontSize: "0.6rem" }} className="ms-1">
									({rider.gender})
								</span>
								{rider.status === RiderStatus.injured && (
									<span className="ms-1" style={{ fontSize: "0.7rem" }}>
										<FaUserInjured className="mb-1" /> Injured
									</span>
								)}
								{getCountryISO2((rider.country ?? "").toUpperCase().trim()) ? (
									<ReactCountryFlag
										countryCode={getCountryISO2(
											(rider.country ?? "").toUpperCase().trim(),
										)}
										svg
										style={{ width: "2rem" }}
										className="mt-1"
									/>
								) : (
									<span className="mx-2">{rider.country}</span>
								)}
							</div>
							<div className="d-flex flex-row">
								{(rider.ratings ?? []).map((rating: Rating) => {
									if (rating.sport.toString() === sport) {
										return (
											<span key={rating._id}>
												{formatCurrency(calculateRiderValue(rating.rating))}
											</span>
										);
									}
									return null;
								})}
							</div>
						</Col>
						<Col xs={5} className="text-end align-items-center h-100">
							<div className="d-flex gap-1 justify-content-end">
								<RiderHistoryButton
									riderId={rider._id}
									riderName={`${rider.first_name} ${rider.last_name}`}
									initialRider={rider}
									size="sm"
									iconOnly
									className="px-2"
								/>
								<Button
									variant={
										isAddDisabled(rider.gender as Gender, rider)
											? "secondary"
											: "success"
									}
									size="sm"
									className="text-light px-2"
									onClick={() => addRiderToTeam(rider)}
									disabled={isAddDisabled(rider.gender as Gender, rider)}
								>
									Add
								</Button>
							</div>
						</Col>
					</Row>
				</div>
			</Card.Body>
		</Card>
	);
};

export default BudgetModeEligibleRiderCard;
